import { useEffect, useRef, useState } from "react";
import { FileImage, Loader2, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { newDesign } from "@/lib/stickerDesign";
import { useAuth } from "@/context/AuthContext";
import LabelCanvas from "@/components/stickers/LabelCanvas";
import SheetPreview from "@/components/stickers/SheetPreview";
import SheetSetup from "@/components/stickers/SheetSetup";
import ElementPanel from "@/components/stickers/ElementPanel";
import LogoBox from "@/components/stickers/LogoBox";
import DecorPanel from "@/components/stickers/DecorPanel";
import { DEFAULT_DECOR } from "@/lib/stickerDesign";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const blobUrl = (url) => api.get(url, { responseType: "blob" }).then((r) => URL.createObjectURL(r.data)).catch(() => null);

export default function StickerDesigner() {
  const { isAdmin } = useAuth();
  const [designs, setDesigns] = useState([]);
  const [id, setId] = useState("");
  const [design, setDesign] = useState(newDesign());
  const [selected, setSelected] = useState(null);
  const [biz, setBiz] = useState({});
  const [logo, setLogo] = useState(null);
  const [template, setTemplate] = useState(null);
  const [busy, setBusy] = useState("");
  const file = useRef(null);

  const loadDesigns = () => api.get("/branding/sticker-designs").then((r) => { setDesigns(r.data); return r.data; });
  useEffect(() => {
    api.get("/invoice-settings").then((r) => setBiz(r.data)).catch(() => {});
    blobUrl("/branding/logo").then(setLogo);
    loadDesigns().then((list) => list[0] && open(list[0]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (d) => {
    setId(d?.id || ""); setDesign(d ? { ...d } : newDesign()); setSelected(null);
    setTemplate(d?.has_template ? await blobUrl(`/branding/sticker-designs/${d.id}/template`) : null);
  };
  const run = async (k, fn) => { setBusy(k); try { await fn(); } catch (e) { toast.error(formatError(e)); } setBusy(""); };
  const body = () => ({ name: design.name.trim(), sheet: design.sheet, elements: design.elements, decor: design.decor, template_opacity: design.template_opacity });

  const save = () => run("save", async () => {
    const { data } = id ? await api.put(`/branding/sticker-designs/${id}`, body()) : await api.post("/branding/sticker-designs", body());
    setId(data.id); await loadDesigns(); toast.success(`Design "${data.name}" saved`);
  });
  const remove = () => run("delete", async () => {
    if (!window.confirm(`Delete design "${design.name}"?`)) return;
    await api.delete(`/branding/sticker-designs/${id}`); const list = await loadDesigns(); open(list[0]); toast.success("Design deleted");
  });
  const uploadScan = (f) => run("scan", async () => {
    let did = id;
    if (!did) { const { data } = await api.post("/branding/sticker-designs", body()); did = data.id; setId(did); }
    const fd = new FormData(); fd.append("file", f);
    await api.post(`/branding/sticker-designs/${did}/template`, fd);
    setTemplate(await blobUrl(`/branding/sticker-designs/${did}/template`)); await loadDesigns(); toast.success("Scanned sheet added");
  });
  const removeScan = () => run("scan", async () => { await api.delete(`/branding/sticker-designs/${id}/template`); setTemplate(null); await loadDesigns(); });

  const set = (p) => setDesign((d) => ({ ...d, ...p }));
  return (
    <div className="fade-up space-y-6" data-testid="sticker-designer-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-cyan-400">Labels</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">Stickers</h1>
        <p className="text-sm text-muted-foreground mt-2">Design your stickers, line them up with a scan of your sticker sheet, then pick the design when you click Print stickers in a table.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={id || "new"} onValueChange={(v) => open(v === "new" ? null : designs.find((d) => d.id === v))}>
          <SelectTrigger className="w-60" data-testid="design-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card">
            <SelectItem value="new">+ New design</SelectItem>
            {designs.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={design.name} onChange={(e) => set({ name: e.target.value })} maxLength={80} className="w-56" data-testid="design-name-input" />
        {isAdmin && <Button onClick={save} disabled={!!busy || !design.name.trim()} data-testid="design-save-button">{busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save design</Button>}
        {isAdmin && id && <Button variant="ghost" className="text-destructive" onClick={remove} disabled={!!busy} data-testid="design-delete-button"><Trash2 size={14} /> Delete</Button>}
      </div>
      <div className="grid xl:grid-cols-[1fr_360px] gap-6">
        <LabelCanvas design={design} setElements={(elements) => set({ elements })} biz={biz} logo={logo} selected={selected} setSelected={setSelected} />
        <div className="space-y-4">
          {isAdmin && <LogoBox logo={logo} setLogo={setLogo} decor={{ ...DEFAULT_DECOR, ...design.decor }} setDecor={(decor) => set({ decor })} />}
          <ElementPanel elements={design.elements} setElements={(elements) => set({ elements })} selected={selected} setSelected={setSelected} />
        </div>
      </div>
      <div className="grid xl:grid-cols-[360px_1fr] gap-6">
        <div className="space-y-4">
          <SheetSetup sheet={design.sheet} setSheet={(sheet) => set({ sheet })} />
          <DecorPanel decor={{ ...DEFAULT_DECOR, ...design.decor }} setDecor={(decor) => set({ decor })} />
          <div className="border rounded-md p-3 space-y-2" data-testid="scan-template">
            <p className="text-sm font-medium">Scanned sticker sheet</p>
            <p className="text-xs text-muted-foreground">Scan a blank sheet at full page size (PNG or JPG). It only shows here to line things up and never prints.</p>
            {isAdmin && <input ref={file} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => { e.target.files[0] && uploadScan(e.target.files[0]); e.target.value = ""; }} data-testid="scan-file-input" />}
            <div className="flex gap-2">
              {isAdmin && <Button size="sm" variant="outline" onClick={() => file.current.click()} disabled={!!busy} data-testid="scan-upload-button">{busy === "scan" ? <Loader2 size={14} className="animate-spin" /> : <FileImage size={14} />} {template ? "Replace scan" : "Upload scan"}</Button>}
              {isAdmin && template && <Button size="sm" variant="ghost" onClick={removeScan} disabled={!!busy} data-testid="scan-remove-button"><X size={14} /> Remove</Button>}
            </div>
            {template && <label className="text-xs flex items-center gap-2">Scan visibility
              <input type="range" min={0} max={1} step={0.05} value={design.template_opacity} onChange={(e) => set({ template_opacity: Number(e.target.value) })} data-testid="scan-opacity" /></label>}
          </div>
        </div>
        <SheetPreview design={design} setSheet={(sheet) => set({ sheet })} template={template} biz={biz} logo={logo} />
      </div>
    </div>
  );
}
